import { useMemo, useState, type JSX } from 'react'
import { type ViewMetadata } from '@/types'
import { ViewHeader, View } from '@/components'
import { TabsContent } from '@ttab/elephant-ui'
import { TableProvider } from '@/contexts/TableProvider'
import { TableCommandMenu } from '@/components/Commands/TableCommand'
import { Header } from '@/components/Header'
import { Commands } from '@/components/Commands'
import { factboxColumns } from './FactboxColumns'
import type { Factbox } from '@/shared/schemas/factbox'
import { FactboxList } from './FactboxList'
import { useRegistry } from '@/hooks/useRegistry'
import { useInitFilters } from '@/hooks/useInitFilters'
import { useTranslation } from 'react-i18next'

const meta: ViewMetadata = {
  name: 'Factboxes',
  path: `${import.meta.env.BASE_URL}/factboxes`,
  widths: {
    sm: 12,
    md: 12,
    lg: 6,
    xl: 6,
    '2xl': 6,
    hd: 6,
    fhd: 4,
    qhd: 3,
    uhd: 2
  }
}

export const Factboxes = (): JSX.Element => {
  const [currentTab, setCurrentTab] = useState<string>('list')
  const { locale, timeZone } = useRegistry()
  const { t } = useTranslation('views')

  const columns = useMemo(() =>
    factboxColumns({ locale, timeZone, t }), [locale, timeZone, t])

  const columnFilters = useInitFilters<Factbox>({
    path: 'filters.Factboxes.current',
    columns
  })

  return (
    <View.Root tab={currentTab} onTabChange={setCurrentTab}>
      <TableProvider<Factbox>
        columns={columns}
        type={meta.name}
        initialState={{ columnFilters }}
      >
        <TableCommandMenu heading='Factboxes'>
          <Commands />
        </TableCommandMenu>

        <ViewHeader.Root>
          <ViewHeader.Content>
            <ViewHeader.Title name='Factboxes' title={t('factboxes.title')} />
            <Header type='Factbox' docType='core/factbox' />
          </ViewHeader.Content>

          <ViewHeader.Action />
        </ViewHeader.Root>

        <View.Content>
          <TabsContent value='list' className='mt-0'>
            <FactboxList columns={columns} />
          </TabsContent>
        </View.Content>
      </TableProvider>
    </View.Root>
  )
}

Factboxes.meta = meta
